/**
 * ReputationHistorySection — creator metric trends as sparklines.
 *
 * Layout: 1 col on mobile, max 2 per row from `sm` up. One card per metric.
 * Each card shows label + headline value + sparkline (with axes + per-point
 * hover/tap readout) + delta. Skipped when reputation_history has <2 entries.
 *
 * Metrics shown (in order, skipping ones with <2 non-null values):
 *   - Followers     (follower_count — IG-dominant)
 *   - Subscribers   (subscriber_count — YT-dominant)
 *   - Following     (following_count)
 *   - Avg Likes     (average_likes)
 *   - Avg Comments  (average_comments)
 *   - Avg Views     (average_views)
 *   - Total Likes   (total_likes)
 *   - Total Views   (total_views)
 *
 * Most creators will see 4-6 cards. YT creators typically have
 * subscribers + total_views populated; IG creators typically have
 * follower_count + average_likes/comments populated.
 */
import { useMemo } from 'react';
import type { InsightIQProfile } from '@discovr/types';
import { SectionTitle } from '../charts/StatCard';
import { Sparkline } from '../charts/Sparkline';
import { InfoTip } from '../charts/InfoTip';
import { fmtNum, fmtDecimal2 } from '../utils/formatters';

interface ReputationHistorySectionProps {
  profile: InsightIQProfile;
}

interface MetricPoint {
  month: string;
  value: number;
}

interface MetricSeries {
  label: string;
  points: MetricPoint[];
  current: number;
  delta: number;
  color: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2025-11" → "Nov '25". Falls back to the raw string if unparseable. */
function fmtMonth(m: string | undefined): string {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const idx = Number(mo) - 1;
  if (!y || idx < 0 || idx > 11) return m;
  return `${MONTHS[idx]} '${y.slice(2)}`;
}

const METRIC_COLOR = {
  followers: '#3b82f6',  // blue
  likes:     '#22c55e',  // green
  views:     '#9647f8',  // violet
  totalViews:'#EC4899',  // magenta
  subs:      '#0ea5e9',  // sky
  following: '#9647f8',  // purple
  comments:  '#f97316',  // orange
  totalLikes:'#dc2626',  // rose
} as const;

export function ReputationHistorySection({ profile }: ReputationHistorySectionProps) {
  // Memoized so the `?? []` fallback doesn't hand `series` a fresh array
  // reference every render (which would defeat its useMemo below).
  const rows = useMemo(
    () => profile.reputation_history ?? [],
    [profile.reputation_history],
  );

  const series: MetricSeries[] = useMemo(() => {
    if (rows.length < 2) return [];

    const build = (
      label: string,
      pluck: (r: typeof rows[number]) => number | null | undefined,
      color: string,
    ): MetricSeries | null => {
      // Keep month↔value paired so null-drop doesn't misalign the x-axis labels.
      const points: MetricPoint[] = rows
        .map((r) => ({ month: r.month, value: pluck(r) }))
        .filter((p): p is MetricPoint => p.value != null);
      if (points.length < 2) return null;
      const first = points[0]?.value ?? 0;
      const last = points[points.length - 1]?.value ?? 0;
      const delta = first === 0 ? 0 : ((last - first) / first) * 100;
      return { label, points, current: last, delta, color };
    };

    // Order mirrors Figma figd_2: Subscribers, Avg Views, Total Views, Avg
    // Likes, Avg Comments (Followers/Following/Total Likes kept as extra cards
    // for IG creators / admin, rendered after the Figma five when present).
    return [
      build('Subscribers',  (r) => r.subscriber_count,  METRIC_COLOR.subs),
      build('Avg Views',    (r) => r.average_views,     METRIC_COLOR.views),
      build('Total Views',  (r) => r.total_views,       METRIC_COLOR.totalViews),
      build('Avg Likes',    (r) => r.average_likes,     METRIC_COLOR.likes),
      build('Avg Comments', (r) => r.average_comments,  METRIC_COLOR.comments),
      build('Followers',    (r) => r.follower_count,    METRIC_COLOR.followers),
      build('Following',    (r) => r.following_count,   METRIC_COLOR.following),
      build('Total Likes',  (r) => r.total_likes,       METRIC_COLOR.totalLikes),
    ].filter((s): s is MetricSeries => s !== null);
  }, [rows]);

  if (series.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <SectionTitle>
        Reputation History
        <InfoTip label="What is Reputation History?">
          How this creator&apos;s numbers have moved month to month — followers, likes, comments,
          views. Each card shows where they are now and how much that&apos;s changed. Hover any
          point to see a specific month.
        </InfoTip>
      </SectionTitle>
      {/* Subtitle copy per Figma figd_2 (replaces the "Last N months · …" range). */}
      <div className="text-body-sm-regular text-[#737373] mb-4">
        Full Profile Analytics Breakdown
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {series.map((s) => (
          <MetricCard key={s.label} series={s} id={`rep-${s.label.toLowerCase().replace(/\s+/g, '-')}`} />
        ))}
      </div>
    </div>
  );
}

function MetricCard({ series, id }: { series: MetricSeries; id?: string }) {
  const deltaSign = series.delta >= 0 ? '↑' : '↓';
  const deltaColor = series.delta >= 0 ? 'text-green-600' : 'text-red-500';
  const deltaDisplay = fmtDecimal2(Math.abs(series.delta));
  const values = series.points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const tooltips = series.points.map((p) => `${fmtMonth(p.month)} · ${fmtNum(p.value)}`);
  const startMonth = fmtMonth(series.points[0]?.month);
  const endMonth = fmtMonth(series.points[series.points.length - 1]?.month);
  return (
    <div id={id} className="scroll-mt-4 rounded-xl border border-[#E5E5E5] bg-white p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          {series.label}
        </span>
        {deltaDisplay != null && (
          <span className={`text-[11px] font-bold ${deltaColor}`}>
            {deltaSign} {deltaDisplay}%
          </span>
        )}
      </div>
      <div className="text-heading-h7 text-foreground leading-none">
        {fmtNum(series.current)}
      </div>
      <div className="mt-2">
        <Sparkline
          values={values}
          color={series.color}
          height={40}
          axisLabels={{ min: fmtNum(min), max: fmtNum(max) }}
          tooltips={tooltips}
        />
      </div>
      {/* X-axis: the time range these points span. */}
      <div className="mt-1 flex justify-between pl-[30px] text-[10px] font-medium text-muted-foreground">
        <span>{startMonth}</span>
        <span>{endMonth}</span>
      </div>
    </div>
  );
}
